import { type Ref, forwardRef, useMemo } from "react";
import { GroupedVirtuoso, type GroupedVirtuosoHandle, type GroupedVirtuosoProps } from "react-virtuoso";
import { VirtuosoGroup } from "./Group";
import { VirtuosoItem } from "./Item";
import { VirtuosoList } from "./List";
import { GroupedVirtualizedProvider, type IGroupedVirtualizedContext } from "./context";

export type IGroupedVirtualizedProps<D, C> = Omit<GroupedVirtuosoProps<D, C>, "data" | "groupCounts"> &
  IGroupedVirtualizedContext & {
    groupCounts: Exclude<GroupedVirtuosoProps<D, C>["groupCounts"], undefined>;
    data: Exclude<GroupedVirtuosoProps<D, C>["data"], undefined>;
    fixedHeader?: boolean;
  };

/**
 * filledData is created to fix a bug occuring with the data props in grouped mod
 *
 * Groups headers are counted as data items by virtuoso.
 * If you have 4 groups of 1 item each and your data array only contains those 4 items,
 * only 2 of them will display with their respective headers.
 * Because those 2 items with those 2 groups header will already hit the total of 4.
 *
 * To prevent this issue, we are adding 1 dummy item at the start of each group to take
 * into account each groups header. So at the end, the size of our data array equals
 * the number of item plus the number of groups.
 */

export const GroupedVirtualized = forwardRef(function GroupedVirtualized<D, C>(
  { slotProps, data, groupCounts, fixedHeader, ...props }: IGroupedVirtualizedProps<D, C>,
  ref?: Ref<GroupedVirtuosoHandle>
) {
  const { Header, ...components } = props.components ?? {};
  const filledData = useMemo(() => {
    const filledData: D[] = [];
    let index = 0;
    let indexTarget = 0;
    for (const groupSize of groupCounts) {
      indexTarget += groupSize;
      filledData.push(data[index]);
      while (index < indexTarget) {
        filledData.push(data[index]);
        ++index;
      }
    }
    return filledData;
  }, [data, groupCounts]);

  return (
    <GroupedVirtualizedProvider slotProps={slotProps}>
      {fixedHeader && Header && <Header />}
      <GroupedVirtuoso
        {...props}
        data={filledData}
        groupCounts={groupCounts}
        components={{
          List: VirtuosoList,
          Item: VirtuosoItem,
          Group: VirtuosoGroup,
          ...(!fixedHeader && Header && { Header }),
          ...components,
        }}
        ref={ref}
      />
    </GroupedVirtualizedProvider>
  );
});
